import apiV2 from '../../api/v2';
import type { SdiVersion } from './sdi';
import { buildClinicalFilename, extractClinicalPatientFileId } from './clinical-filenames';

// Upper bound on RDI versions fetched for the version selector.
const RDI_VERSION_LIMIT = 50;

interface RawTimelineEvent {
    id: string;
    version: number;
    status: string;
    createdAt: string;
    payload?: Record<string, unknown>;
}

function buildRdiFilename(payload: Record<string, unknown> | undefined, version: number): string {
    return buildClinicalFilename('RDI', 'rdiPdf', payload, version);
}

function extractRdiPatientFileId(payload: Record<string, unknown> | undefined): string | null {
    return extractClinicalPatientFileId('rdiPdf', payload);
}

/**
 * List all RDI submission versions for a patient (latest-first), mirroring
 * fetchSdiVersions. Only active events with an attached PDF are returned.
 */
export async function fetchRdiVersions(patientId: string): Promise<SdiVersion[]> {
    const response = await apiV2.get<{ data: RawTimelineEvent[] }>(
        `/patients/${encodeURIComponent(patientId)}/timeline`,
        { params: { type: 'rdi_submission', limit: RDI_VERSION_LIMIT } }
    );
    const events = response.data?.data ?? [];
    return (
        events
            .filter((e) => e.status === 'active')
            .map((e) => {
                const patientFileId = extractRdiPatientFileId(e.payload);
                if (!patientFileId) return null;
                return {
                    id: e.id,
                    version: e.version,
                    createdAt: e.createdAt,
                    patientFileId,
                    fileName: buildRdiFilename(e.payload, e.version)
                };
            })
            .filter((v): v is SdiVersion => v !== null)
            // Documents imported off Monday all carry version 0, so the version number
            // alone cannot order them — fall back to the date they were filed.
            .sort((a, b) => b.version - a.version || b.createdAt.localeCompare(a.createdAt))
    );
}
